import { createMuiTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { ThemeProvider } from '@material-ui/styles'
// import * as Sentry from '@sentry/browser'
import { parseISO } from 'date-fns'
import json2mq from 'json2mq'
import MobileDetect from 'mobile-detect'
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { ReadyState } from 'react-use-websocket'
import 'url-polyfill'
import ChatWindow from './ChatWindow'
import ConversationHistory from './ConversationHistory'
import { useSimpleReducer } from './js/app-helpers'
import { SessionContext } from './session-context'
import './css/main.css'
import theme from './theme'
import useChat from './UseChat'

import {
  createConversationWithAgent,
  getConversation,
  getConversationEvents,
  getItem,
  postMessage,
  loadConversations,
  requestBotByFlowId,
  requestBotById,
  sendChat,
  setItem,
  removeItem
} from './js/data-providers'
import ChatPrompt from './ChatPrompt'

function App (props) {
  const { setToken, setChatURL, welcomePacket, setWelcomePacket, activeConversationId, setActiveConversationId } =
    useContext(SessionContext)
  const worker = props.worker
  const packet_code = props.packet_code
  const storagePrefix = props.storagePrefix
  const session_id = props.session_id
  const visit_id = props.visit_id
  const chatServiceUrl = props.chatServiceUrl
  const wsURL = props.wsURL
  const preventCriteriaPrompts = props.preventCriteriaPrompts
  const supportBot = props.supportBot
  const [showConversationHistory, setShowConversationHistory] = useState(false)
  const token = `visitor_${session_id}_${packet_code}`
  const [customTheme, setCustomTheme] = useState(null)
  const page_load_id = props.page_load_id
  const page_url = props.page_url
  const referrer = props.referrer
  const page_title = props.page_title
  const hide_mode = props.hide_mode
  const identity = props.identity
  const visualViewportSize = props.visualViewportSize
  const autoLoadBotId = props.autoLoadBotId
  const submissionID = props.submissionID
  const hasAutoLoadedByIdRef = useRef(false)
  const metaAttributes = props.metaAttributes
  const pageRendered = useRef(false)

  const [majorChatEvents, setMajorChatEvents, resetMajorChatEvents] = useSimpleReducer({
    /** If an agent has been routed to the conversation or attempted */
    agentBeenRouted: false,
    /** If an agent has chatted at anytime during or after bot flow */
    agentHasChatted: false,
    /** If the bot flow has ended */
    botFlowEnded: false
  })

  const [readReceipts, setReadReceipts] = useState({})
  const [activeConversation, setActiveConversation] = useState(null)
  const [windowClosed, setWindowClosed] = useState(false)

  const chatBoxRef = useRef()
  const chatBoxWrapRef = useRef()

  let savedConversationId = null
  if (props.mode !== 'test-drive' && storagePrefix !== 'formbot') {
    savedConversationId = getItem(`${storagePrefix}active-convo-id-${packet_code}`)
  }
  let startingDisplayState = 'icon'

  let fullScreen = welcomePacket && welcomePacket.chat_size === 'fullscreen'

  if (['content', 'content-with-header', 'landing-page'].includes(props.mode) || props.mode.startsWith('modal')) {
    fullScreen = true
    startingDisplayState = 'open'
  } else if (props.mode === 'content-phone') {
    startingDisplayState = 'mini'
  } else if (savedConversationId) {
    startingDisplayState = 'open'
  }

  const [displayState, _setDisplayState] = useState(startingDisplayState)
  const [activeConversationMessageParticipants, setActiveConversationMessageParticipants] = useState([])
  const [conversationList, setConversationList] = useState([])
  const [activeConversationMessages, setActiveConversationMessages] = useState([])
  const [botHasFired, setBotHasFired] = useState(false)
  const [botMessageQueue, setBotMessageQueue] = useState([])
  const [botReady, setBotReady] = useState(true)
  const [unconnected, setUnconnected] = useState(false)
  const [currentTypingEvent, setCurrentTypingEvent] = useState(null)
  const [currentKind, setCurrentKind] = useState(null)
  const [botParticipantID, setBotParticipantID] = useState(null)
  const [activeBotID, setActiveBotID] = useState(null)
  const [cloudAgentName, setCloudAgentName] = useState(welcomePacket?.bot_name || '')
  const [cloudAgentAvatar, setCloudAgentAvatar] = useState(welcomePacket?.bot_avatar_url || '')
  const conversationSearchParamsRef = useRef({ searchFilter: 'mine_open', searchText: '', sortOrder: 'newest' })

  var md = new MobileDetect(window.navigator.userAgent)
  const phone = !!md.phone()

  const pageSize = 5

  useEffect(() => {
    postMessage('appLoaded')
  }, [])
  useEffect(() => {
    setToken(token)
    setChatURL(chatServiceUrl)
    setWelcomePacket(props.welcomePacket)
    setActiveConversationId(savedConversationId)
  }, [])

  useEffect(() => {
    if (activeConversationId) {
      resetMajorChatEvents()
    }
    // eslint-disable-next-line
  }, [activeConversationId])

  function SendBotMessage () {
    const message = botMessageQueue[0]
    const kind = message.attributes.kind
    let delay = 800

    if (kind === 'cta_button') {
      delay = 100
    }

    const typingEvent = {
      type: 'conversation_events',
      attributes: {
        body: '',
        kind: 'type',
        created_timestamp: message.attributes.created_timestamp,
        data: {}
      },
      relationships: {
        from_participant: {
          data: {
            type: 'participants',
            id: botParticipantID
          }
        }
      }
    }
    setBotReady(false)
    const newBotMessages = [...botMessageQueue]
    newBotMessages.splice(0, 1)
    setBotMessageQueue(newBotMessages)
    if (kind !== 'email_prompt' && kind !== 'cta_button' && kind !== 'option_prompt' && kind !== 'option_prompt_free') {
      if (kind === 'chat' && currentKind !== 'chat') {
        setCurrentTypingEvent(typingEvent)
        setCurrentKind(kind)
      }
    }
    if (kind === 'chat' && (currentKind === 'chat' || currentKind === 'type')) {
      setCurrentKind(null)
      setCurrentTypingEvent(null)
      setActiveConversationMessages((prev) => dedupeAppend(prev, message))
      setBotReady(true)
      return
    }
    setTimeout(() => {
      setCurrentKind(null)
      setCurrentTypingEvent(null)
      setActiveConversationMessages((prev) => dedupeAppend(prev, message))
      setBotReady(true)
    }, delay)
  }

  useEffect(() => {
    setTimeout(() => {
      if (botReady && botMessageQueue.length) {
        SendBotMessage()
      }
    }, 400)
  }, [botMessageQueue, botReady])

  const performConversationSearch = useCallback(
    ({ searchFilter, searchText, sortOrder }) => {
      const page = 1
      loadConversations({
        chatServiceUrl,
        token,
        searchText,
        searchFilter,
        sortOrder,
        page,
        pageSize
      }).then((response) => {
        const receipts = response.included
        let receiptsDict = {}
        if (receipts) {
          receipts.map(
            (receipt) => (receiptsDict = { ...receiptsDict, [receipt.id]: receipt.attributes.read === 'True' })
          )
          if (activeConversationId) {
            receiptsDict[activeConversationId] = true
          }
        }
        setConversationList(response.data)
        setReadReceipts(receiptsDict)
      })
    },
    [token, chatServiceUrl, pageSize]
  )

  const performConversationSearchWithCurrentParams = useCallback(() => {
    performConversationSearch(conversationSearchParamsRef.current)
  }, [performConversationSearch])

  const loadConversationEvents = useCallback(() => {
    if (activeConversationId) {
      getConversationEvents({
        conversationID: activeConversationId,
        token,
        chatServiceUrl
      }).then((result) => {
        setActiveConversationMessages(result.data)
        setActiveConversationMessageParticipants(result.included)
      })
    } else {
      setActiveConversationMessages([])
      setActiveConversationMessageParticipants([])
    }
  }, [activeConversationId, token, chatServiceUrl])

  useEffect(() => {
    // This is a bandaid for a bug I've yet been able to track down. Archive360 has a bug where the conversation
    // events occassionally get reset for the visitor.
    if (activeConversationMessages.length === 0 && activeConversationId) {
      setTimeout(() => {
        loadConversationEvents()
      }, 1000)
    }
  }, [activeConversationId, loadConversationEvents, activeConversationMessages])

  const loadConversation = useCallback(() => {
    if (activeConversationId) {
      getConversation({
        conversationID: activeConversationId,
        chatServiceUrl,
        token
      }).then((response) => {
        const activeConversationParticipant = response.included.find(
          (i) => i.attributes && (i.attributes.kind === 'person' || i.attributes.kind === 'visitor')
        )
        const activeConversationParticipantId = activeConversationParticipant.id
        const p = {
          participantID: activeConversationParticipantId,
          personID: null
        }
        if (
          response.data &&
          response.data.relationships &&
          response.data.relationships.for_person &&
          response.data.relationships.for_person.data.id
        ) {
          p.personID = response.data.relationships.for_person.data.id
        }
        setActiveConversation(response.data)
      })
    } else {
      setActiveConversation(null)
    }
  }, [activeConversationId, token, chatServiceUrl])

  useEffect(() => {
    loadConversationEvents()
    loadConversation()
  }, [activeConversationId, loadConversationEvents, loadConversation])

  const dedupeAppend = (existing_array, new_item) => {
    const match = existing_array.filter((i) => {
      if (i.id === new_item.id) {
        return true
      }
      return false
    })
    if (!match.length) {
      return existing_array.concat([new_item])
    }
    return existing_array
  }

  function handleConversationEvent (data) {
    const conversationID = data.relationships.conversation.data.id
    const newChatMessage = { ...data }
    const messageType = newChatMessage.attributes.kind
    if (messageType === 'open') {
      return
    }
    const sender = newChatMessage.relationships.from_participant.data.id
    if (sender === botParticipantID) {
      setBotHasFired(true)
    }
    if (messageType === 'chat') {
      newMessageRef.current()
      if (activeConversation && activeConversation.attributes.meta.last_agent_participant_id !== sender) {
        if (newChatMessage.attributes.participant_name !== '') {
          loadConversation()
        }
      }
    }

    if (conversationID === activeConversationId) {
      if (
        sender === botParticipantID &&
        ['chat', 'calendar_drop', 'option_prompt', 'option_prompt_free', 'rating_drop', 'email_prompt', 'cta_button'].includes(messageType)
      ) {
        setBotMessageQueue((prev) => prev.concat(newChatMessage))
      } else {
        setActiveConversationMessages((prev) => dedupeAppend(prev, newChatMessage))
      }
      if (activeConversation) {
        if (sender !== activeConversation.relationships.for_participant.data.id) {
          setCurrentKind(messageType)
          if (messageType === 'type') {
            setCurrentTypingEvent(newChatMessage)
          } else if (messageType === 'chat' || messageType === 'halt') {
            setCurrentTypingEvent(null)
          }
        }
      }
    }
  }

  const messageCallback = useCallback(
    (messageObject) => {
      const data = JSON.parse(messageObject.data)
      switch (data.type) {
        case 'conversation_events':
          handleConversationEvent(data)
          break
        case 'blocked_participants':
          props.hideApp()
          postMessage('block_participant', data.attributes)
          break
        case 'hide_chat_window':
          props.hideApp()
          postMessage('page_is_blocked', data.attributes)
          break
        case 'unhide_chat_window':
          props.unhideApp()
          break
        case 'chat_notifications':
          break
        case 'route':
          performConversationSearchWithCurrentParams()
          break
        case 'prompt':
          if (!preventCriteriaPrompts) {
            if (phone && data.mobile_style) {
              data.style = data.mobile_style
            }
            const storedConversationID = getItem(`${storagePrefix}active-convo-id-${packet_code}`)
            if (displayState !== 'open' || storedConversationID !== data.conversation_id) {
              if (displayState !== 'open') {
                setDisplayState(data.style)
              }
              setActiveConversationId(data.conversation_id)
              if (displayState === 'open' || data.style === 'open') {
                setItem(`${storagePrefix}active-convo-id-${packet_code}`, data.conversation_id)
              }
            }
            setActiveBotID(data.bot_id)
            postMessage('ga', {
              action: 'Bot Prompt',
              label: 'prompt'
            })
          }
          break
        case 'new_conversation':
          if (displayState !== 'open') {
            setDisplayState('big')
            setActiveConversationId(data.conversation_id)
          }
          break
        case 'ping':
          break
        default:
          break
      }
    },
    [
      activeConversationId,
      performConversationSearchWithCurrentParams,
      displayState,
      loadConversation,
      activeConversationMessages
    ]
  )

  function setDisplayState (size) {
    if (props.mode !== 'landing-page') {
      if (size === 'open' || size === 'big') {
        setTimeout(() => _setDisplayState(size), 80)
      } else {
        _setDisplayState(size)
      }
      changeFrameSize(size)
      if (size === 'open' && activeConversationIdRef && activeConversationIdRef.current) {
        setItem(`${storagePrefix}active-convo-id-${packet_code}`, activeConversationIdRef.current)
      }
    }
  }

  useEffect(() => {
    if (props.mode !== 'landing-page') {
      changeFrameSize(displayState)
    }
  }, [displayState])

  const promptReceivedRef = useRef(null)
  const promptReceived = (command) => {
    setDisplayState(command.style)
  }
  promptReceivedRef.current = promptReceived

  const displayStateRef = useRef(null)
  displayStateRef.current = displayState

  const newMessage = () => {
    postMessage('new_message', { displayState: displayState })
  }

  const newMessageRef = useRef(null)
  newMessageRef.current = newMessage
  const botTriggerByFlowIdRef = props.botTriggerByFlowIdRef
  const botTriggerByIdRef = props.botTriggerByIdRef
  const startConversationWithUserIdRef = props.startConversationWithUserIdRef
  const connectionStatusRef = useRef(null)
  const {
    subscribeToConversations,
    syncScrollPercent,
    syncURLchange,
    setDebug,
    connectionStatus,
    setIdentity,
    setMetaAttribute,
    requestBot
  } = useChat({
    activeConversationId,
    chatServiceUrl,
    displayState,
    identity,
    messageCallback,
    newMessageRef,
    packet_code,
    page_load_id,
    page_title,
    page_url,
    wsURL,
    preventCriteriaPrompts,
    promptReceivedRef,
    referrer,
    session_id,
    storagePrefix,
    supportBot,
    visit_id,
    worker
  })

  useEffect(() => {
    if (metaAttributes && connectionStatus === ReadyState.OPEN) {
      for (const key in metaAttributes) {
        setMetaAttribute(key, metaAttributes[key])
      }
    }
  }, [metaAttributes, setMetaAttribute, connectionStatus])

  useEffect(() => {
    if (identity && connectionStatus === ReadyState.OPEN) {
      setIdentity(identity)
    }
  }, [identity, setIdentity, connectionStatus])

  useEffect(() => {
    if (connectionStatus === ReadyState.OPEN) {
      const conversationIDs = conversationList.map((i) => i.id)
      conversationIDs.push(activeConversationId)
      subscribeToConversations(conversationIDs)
    }
  }, [activeConversationId, conversationList, subscribeToConversations, connectionStatus])

  connectionStatusRef.current = connectionStatus

  useEffect(() => {
    if (connectionStatus === ReadyState.OPEN) {
      if (props.debug !== undefined) {
        setDebug(props.debug)
      }
      window.setDebug = (debug) => {
        setDebug(debug)
      }
    }
  }, [connectionStatus, props.debug])

  useEffect(() => {
    if (connectionStatusRef.current !== 1) {
      setTimeout(() => {
        if (connectionStatusRef.current !== 1) {
          setUnconnected(true)
        }
      }, 5000)
    } else {
      setUnconnected(false)
    }
  }, [connectionStatus])

  const scrollCallbackRef = props.scrollCallbackRef
  scrollCallbackRef.current = (scroll_percent) => {
    syncScrollPercent(scroll_percent)
  }

  const urlHashCallbackRef = props.urlHashCallbackRef
  urlHashCallbackRef.current = ({ url, page_title, plid, referrer }) => {
    syncURLchange(url, page_title, plid, referrer)
  }

  botTriggerByFlowIdRef.current = (flow_id) => {
    if (!activeConversationId) {
      requestBotByFlowId({ flow_id, token, chatServiceUrl, page_url, referrer, page_title }).then((result) => {
        setActiveConversationId(result.data.id)
        setItem(`${storagePrefix}active-convo-id-${packet_code}`, result.data.id)
        setDisplayState('open')
        postMessage('ga', {
          action: 'Prompt Clicked',
          label: `FLow ID: ${flow_id}`
        })
      })
    }
  }

  // TODO: Check if this causes multiple prompts
  botTriggerByIdRef.current = ({ botID, preview, promptState, submissionID }) => {
    console.log('botTriggerByIdRef', { botID, preview, promptState })
    if (botID === activeBotID) {
      console.log(`bot ${botID} is already active`)
      removeItem(`${storagePrefix}closed-window-${packet_code}`)
      setWindowClosed(false)
      setDisplayState('open')
      return
    }
    requestBotById({ botID, token, chatServiceUrl, page_url, page_title, referrer, preview, submissionID }).then(
      (result) => {
        console.log('requestByById', result)
        setActiveConversationId(result.data.id)
        setItem(`${storagePrefix}active-convo-id-${packet_code}`, result.data.id)
        removeItem(`${storagePrefix}closed-window-${packet_code}`)
        setWindowClosed(false)
        const state = promptState || 'open'
        setDisplayState(state)
        setActiveBotID(botID)
        if (result.meta?.bot?.name) {
          setCloudAgentName(result.meta.bot.name)
        }
        if (result.meta?.bot?.avatar_url) {
          setCloudAgentAvatar(result.meta.bot.avatar_url)
        }
        postMessage('ga', {
          action: 'Bot Prompt',
          label: `Bot ID: ${botID}`
        })
      }
    )
  }

  startConversationWithUserIdRef.current = ({ userID }) => {
    createConversationWithAgent({
      first_message: '',
      prompt: '',
      chatServiceUrl,
      token,
      url: page_url,
      referrer,
      page_title,
      route_to_user_id: userID
    }).then((result) => {
      setActiveConversationId(result.data.id)
      setDisplayState('open')
    })
  }

  useEffect(() => {
    console.log('autoLoad', { autoLoadBotId, hasAutoLoadedByIdRef: hasAutoLoadedByIdRef.current, submissionID })
    if (autoLoadBotId && !hasAutoLoadedByIdRef.current) {
      hasAutoLoadedByIdRef.current = true
      const promptState = props.mode === 'content-phone' ? 'mini' : 'open'
      botTriggerByIdRef.current({ botID: autoLoadBotId, promptState, submissionID })
    }
  }, [autoLoadBotId, botTriggerByIdRef])

  const all_prompts = activeConversationMessages.filter((i) => {
    if (i.attributes.data.bot && i.attributes.data.bot.prompt) {
      return true
    }
  })
  const prompts_to_closed_prompt_events = activeConversationMessages
    .filter((i) => {
      if (i.attributes.data.bot_response && i.attributes.data.bot_response.response_to_event_id) {
        return true
      }
    })
    .reduce((result, item) => {
      result[item.attributes.data.bot_response.response_to_event_id] = item
      return result
    }, {})

  const open_chat_prompts = all_prompts.filter((i) => {
    const close_event = prompts_to_closed_prompt_events[i.id]
    if (close_event) {
      return false
    }
    return true
  })

  let open_chat_prompt
  if (open_chat_prompts.length) {
    open_chat_prompt = open_chat_prompts[open_chat_prompts.length - 1]
  }

  const more_recent_chat_messages = activeConversationMessages.filter((i) => {
    if (!open_chat_prompt) {
      return false
    }
    let ts = i.attributes.created_timestamp
    let prompt_ts = open_chat_prompt.attributes.created_timestamp
    if (!ts || !prompt_ts) {
      return false
    }
    ts = parseISO(ts)
    prompt_ts = parseISO(prompt_ts)
    if (i.attributes.kind === 'chat' && ts > prompt_ts) {
      return true
    }
  })

  useEffect(() => {
    // Only run if it hasn't happened yet
    if (!majorChatEvents.agentBeenRouted) {
      const agentBeenRouted = activeConversationMessages.some((i) => {
        if (
          // Tests if an agent HAS been routed
          (i.attributes.kind === 'join' && i.attributes.data.from === 'route') ||
          // Tests if quick draw event has been created
          i.attributes.kind === 'quick_draw'
        ) {
          return true
        }
        return false
      })
      setMajorChatEvents({ agentBeenRouted })
    }
    // Only run if it hasn't happened yet
    if (!majorChatEvents.agentHasChatted) {
      const agentHasChatted = activeConversationMessages.some((i) => {
        if (i.attributes.kind === 'chat' && i.attributes.participant_name) {
          const metaData = i.attributes.data
          if (
            metaData && // Check if object is empty
            Object.keys(metaData).length === 0 &&
            Object.getPrototypeOf(metaData) === Object.prototype
          ) {
            postMessage('ga', {
              action: 'Agent Chatted',
              label: `Agent: ${i.attributes.participant_name}`
            })
            if (displayState === 'icon') {
              toggleOpenChat()
            } else if (displayState === 'big') {
              setDisplayState('open')
            }
            return true
          }
        }
        return false
      })
      setMajorChatEvents({ agentHasChatted })
    }
    // Only run if it hasn't happened yet
    if (!majorChatEvents.botFlowEnded) {
      const botFlowEnded = activeConversationMessages.some((i) => {
        if (i.attributes.kind === 'bot_end') return true
        return false
      })
      setMajorChatEvents({ botFlowEnded })
    }

    // eslint-disable-next-line
  }, [activeConversationMessages.length])

  /** What type of prompt to display */
  let chatPromptState = 'wait'
  if (majorChatEvents.agentHasChatted || majorChatEvents.agentBeenRouted) {
    chatPromptState = 'chat'
  } else if (majorChatEvents.botFlowEnded) {
    chatPromptState = 'end'
  } else if (open_chat_prompt) {
    if (['option_prompt', 'rating_drop', 'calendar_drop'].includes(open_chat_prompt.attributes.kind)) {
      chatPromptState = 'option'
    } else if (['option_prompt_free'].includes(open_chat_prompt.attributes.kind)) {
      chatPromptState = 'option_free'
    } else {
      chatPromptState = 'chat'
    }
  }

  if (more_recent_chat_messages.length) {
    chatPromptState = 'chat'
    open_chat_prompt = null
  }

  const activeConversationIdRef = useRef(null)
  activeConversationIdRef.current = activeConversationId

  const timeoutRef = useRef(null)

  const small = useMediaQuery(
    json2mq({
      maxDeviceHeight: 800
    })
  )
  const big = useMediaQuery('(min-device-width:2000px)')

  function chooseConversation (conversationID) {
    setActiveConversationId(conversationID)
    setItem(`${storagePrefix}active-convo-id-${packet_code}`, conversationID)
  }

  function sendChatOrCreate (body) {
    if (activeConversationId) {
      if (open_chat_prompt) {
        sendChat({
          conversationID: activeConversationId,
          body,
          data: {
            bot_response: {
              flow_id: open_chat_prompt.attributes.data.bot.flow_id,
              node_id: open_chat_prompt.attributes.data.bot.node_id,
              response_to_event_id: open_chat_prompt.id
            }
          },
          token,
          chatServiceUrl
        }).then(() => {
          postMessage('ga', {
            action: 'Visitor Message Sent',
            label: 'message'
          })
          if (open_chat_prompt.attributes.kind === 'email_prompt') {
            postMessage('ga', {
              action: 'Email Captured',
              label: 'email'
            })
          }
        })
      } else {
        sendChat({ conversationID: activeConversationId, body, data: {}, token, chatServiceUrl }).then(() => {
          postMessage('ga', {
            action: 'Visitor Message Sent',
            label: 'message'
          })
        })
      }
    }
  }
  const sendChatOrCreateRef = useRef(null)
  sendChatOrCreateRef.current = sendChatOrCreate

  const sendChatWrap = useCallback(
    (body, data) => {
      let pass_data = data
      if (!data) {
        pass_data = {}
      }
      sendChat({ conversationID: activeConversationId, body, data: pass_data, token, chatServiceUrl }).then(() => {
        postMessage('ga', {
          action: 'Visitor Message Sent',
          label: 'message'
        })
      })
    },
    [activeConversationId]
  )

  useEffect(() => {
    if (pageRendered.current) {
      performConversationSearchWithCurrentParams()
    }
    pageRendered.current = true
  }, [showConversationHistory])

  function changeFrameSize (frameSize) {
    let x
    let y
    let minHeight
    let maxWidth = '390px'
    let maxHeight = '700px'

    if (welcomePacket && welcomePacket.chat_size === 'fullscreen') {
      maxHeight = '100%'
    }

    if (frameSize === 'big') {
      x = '100%'
      y = '35%'
      minHeight = '262px'
      if (small) {
        y = '55%'
      } else if (big) {
        y = '25%'
      }
    } else if (frameSize === 'open') {
      x = '100%'
      y = '100%'
      minHeight = '280px'

      if (phone) {
        maxWidth = '100%'
        maxHeight = '100%'
      }
    } else if (frameSize === 'mini') {
      x = '342px'
      y = '130px'
      minHeight = '0px'
    } else if (frameSize === 'icon' && !hide_mode) {
      x = '115px'
      y = '95px'
      minHeight = '0px'
    } else if (frameSize === 'icon' && hide_mode) {
      x = '0px'
      y = '0px'
      minHeight = '0px'
    }
    postMessage('resize', {
      x: x,
      y: y,
      minHeight: minHeight,
      maxWidth: maxWidth,
      maxHeight: maxHeight
    })
  }

  useEffect(() => {
    if (props.mode === 'content-phone' || props.mode === 'test-drive' || storagePrefix === 'formbot') {
      return
    }
    const storage_activeConversationID = getItem(`${storagePrefix}active-convo-id-${packet_code}`)
    if (storage_activeConversationID && storage_activeConversationID !== 'null') {
      setActiveConversationId(parseInt(storage_activeConversationID))
      setDisplayState('open')
    }
  }, [])

  function dismissBigPrompt () {
    setItem(`${storagePrefix}big-prompt-dismissed-${packet_code}`, 'yes')
  }

  function setClosedWindow () {
    setItem(`${storagePrefix}closed-window-${packet_code}`, 'yes')
    setWindowClosed(true)
    // setMiniPromptHasBeenDismissed(true)
    setDisplayState('icon')
  }

  function toggleOpenChat () {
    // TODO: use their window size to decide if we need to fullscreen or not
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    if (displayState === 'big') {
      dismissBigPrompt()
      setClosedWindow()
      setDisplayState('icon')
    } else if (displayState === 'open') {
      setClosedWindow()
      setDisplayState('icon')
    } else {
      requestBot()
      removeItem(`closed-window-${packet_code}`)
      setWindowClosed(false)
      setDisplayState('open')
    }
  }

  useEffect(() => {
    if (getItem(`${storagePrefix}closed-window-${packet_code}`) === 'yes') {
      setWindowClosed(true)
      setDisplayState('icon')
    }
    if (windowClosed) {
      setDisplayState('icon')
    }
  }, [windowClosed, displayState])

  const toggleOpenChatRef = useRef(null)
  toggleOpenChatRef.current = toggleOpenChat

  function toggleOpenChatPublic () {
    if (props.mode.startsWith('modal')) {
      postMessage('signals-modal-close')
    } else {
      toggleOpenChatRef.current()
    }
  }

  useEffect(() => {
    if (welcomePacket) {
      const color = welcomePacket.primary_color
      const contrast = welcomePacket.contrast_color
      const button = welcomePacket.button_color || welcomePacket.primary_color

      const customTheme = createMuiTheme({
        palette: {
          primary: {
            main: color,
            contrastText: contrast
          },
          secondary: {
            main: button
          }
        }
      })
      setCustomTheme(customTheme)
      setBotParticipantID(welcomePacket.bot_participant_id)
    }
  }, [welcomePacket])

  useEffect(() => {
    if (!phone) {
      return
    }
    switch (displayStateRef.current) {
      case 'open':
        if (chatBoxRef.current) {
          chatBoxRef.current.style.height = visualViewportSize.height + 'px'
        }
        if (chatBoxWrapRef.current) {
          chatBoxWrapRef.current.style.top = visualViewportSize.offsetTop + 'px'
        }
        break
      default:
        if (chatBoxRef.current) {
          chatBoxRef.current.style.height = ''
        }
        if (chatBoxWrapRef.current) {
          chatBoxWrapRef.current.style.top = ''
        }
    }
  }, [visualViewportSize, phone])

  if (!welcomePacket || !customTheme) {
    return <></>
  }

  let bot = false

  let conversationLabel = ''
  let avatarUrl = null
  const meta = activeConversation?.attributes?.meta
  
  if (meta?.last_agent_name && meta.last_agent_name !== meta.first_agent_name) {
    conversationLabel = meta.last_agent_name
  } else if (cloudAgentName) {
    conversationLabel = cloudAgentName
  } else if (meta?.first_agent_name) {
    conversationLabel = meta.first_agent_name
  } else {
    conversationLabel = meta?.bot_name
  }
  if (meta?.last_agent_avatar && meta.last_agent_avatar !== meta.first_agent_avatar) {
    avatarUrl = meta.last_agent_avatar
  } else if (cloudAgentAvatar) {
    avatarUrl = cloudAgentAvatar
  } else if (meta?.first_agent_avatar) {
    avatarUrl = meta.first_agent_avatar
  } else {
    avatarUrl = welcomePacket.agent_avatar
    bot = true
  }

  if (!activeConversationMessages.length) {
    conversationLabel = cloudAgentName || welcomePacket.bot_name
    avatarUrl = cloudAgentAvatar || welcomePacket.agent_avatar
    bot = true
  }
  // } else if (activeConversation && activeConversation.attributes.meta.last_agent_avatar === null) {
  //   const meta = activeConversation.attributes.meta
  //   if (meta.first_send_type === 'user') {
  //     conversationLabel = meta.first_agent_name
  //     avatarUrl = meta.first_agent_avatar
  //   } else {
  //     conversationLabel = meta.bot_name || welcomePacket.bot_name
  //     avatarUrl = welcomePacket.agent_avatar
  //     bot = true
  //   }
  // }

  function startNewConvo () {
    const botID = welcomePacket.default_bot_profile_id
    botTriggerByIdRef.current({ botID })
  }

  function chatBoxWrapClasses () {
    if (phone) {
      return ['max']
    }
    // const screen = screenRef.current
    // if (screen.x < screen.y && screen.y < 1000) {
    //   return ['max']
    // }
    return []
  }

  let outerClass = 'chat-box-wrap'
  if (displayState === 'big') {
    outerClass = 'chat-box-wrap-prompt'
  } else if (phone) {
    outerClass = 'chat-box-wrap-mobile'
  } else if (fullScreen) {
    outerClass = 'chat-box-wrap-fullscreen'
  }


  const online = welcomePacket.online
  const promptVisibility = welcomePacket.prompt_visibility
  const hidden = (promptVisibility === 'available' && !online) || promptVisibility === 'bot'

  const shouldBeHidden = !activeConversationId && hidden && !botHasFired

  if (shouldBeHidden || (hide_mode && displayState === 'icon')) {
    return <></>
  }

  const chatBoxHeight = fullScreen && displayState === 'open' ? '100%' : null
  let chatBoxClass = fullScreen && displayState === 'open' ? 'fullscreen' : null
  let chatBoxId = 'chat-box-normal'
  if (phone && props.mode !== 'landing-page') {
    chatBoxId = 'chat-box-normal-phone'
    chatBoxClass = displayState === 'open' ? 'phone' : null
  } else if (props.mode === 'content') {
    chatBoxId = 'chat-box-content-landing-page'
    chatBoxClass = 'content-page'
  } else if (props.mode === 'landing-page') {
    if (phone) {
      outerClass = 'chat-box-wrap-fullscreen'
    }
    chatBoxId = 'chat-box-landing-page'
    chatBoxClass = 'landing-page'
    return (
      <ThemeProvider theme={customTheme || theme}>
        <>
          <div id={outerClass} className={chatBoxWrapClasses()} ref={chatBoxWrapRef}>
            <div id="chat-box" className={chatBoxClass}>
              <div id={displayState === 'big' ? 'chat-box-prompt' : chatBoxId} style={{ height: chatBoxHeight }}>
                <ChatWindow
                  mode={props.mode}
                  visualViewportSize={visualViewportSize}
                  openChatPrompt={open_chat_prompt}
                  chatPromptState={chatPromptState}
                  events={activeConversationMessages}
                  participants={activeConversationMessageParticipants}
                  getConversationEvents={loadConversationEvents}
                  activeConversation={activeConversation}
                  avatarUrl={avatarUrl}
                  conversationLabel={conversationLabel}
                  bot={bot}
                  sendChat={sendChatWrap}
                  sendChatOrCreateRef={sendChatOrCreateRef}
                  showConversationHistory={() => setShowConversationHistory(true)}
                  bigPrompt={displayState === 'big'}
                  promptsToClosedPromptEvents={prompts_to_closed_prompt_events}
                  openChatPrompts={open_chat_prompt}
                  phone={phone}
                  closeChat={toggleOpenChatPublic}
                  setDisplayState={setDisplayState}
                  botParticipantID={botParticipantID}
                  unconnected={unconnected}
                  currentTypingEvent={currentTypingEvent}
                  fullScreen={fullScreen}
                  majorChatEvents={majorChatEvents}
                  cloudAgentName={cloudAgentName}
                  cloudAgentAvatar={cloudAgentAvatar}
                />
              </div>
            </div>
          </div>
        </>
      </ThemeProvider>
    )
  }
  chatBoxClass += ' ' + props.mode

  return (
    <ThemeProvider theme={customTheme || theme}>
      <>
        {displayState === 'open' || displayState === 'big' ? (
          <div id={outerClass} className={chatBoxWrapClasses()} ref={chatBoxWrapRef}>
            <div id="chat-box" className={chatBoxClass}>
              <div
                id={displayState === 'big' ? 'chat-box-prompt' : chatBoxId}
                ref={chatBoxRef}
                style={{ height: chatBoxHeight }}
              >
                {showConversationHistory ? (
                  <ConversationHistory
                    setShowConversationHistory={setShowConversationHistory}
                    conversationList={conversationList}
                    setActiveConversationId={chooseConversation}
                    loadConversations={performConversationSearchWithCurrentParams}
                    phone={phone}
                    startNewConvo={startNewConvo}
                    readReceipts={readReceipts}
                    botAvatar={avatarUrl}
                    contrastColor={welcomePacket.contrast_color}
                    primaryColor={welcomePacket.primary_color}
                    disableNewConversation={welcomePacket.disable_new_conversation}
                  />
                ) : (
                  <ChatWindow
                    mode={props.mode}
                    visualViewportSize={visualViewportSize}
                    openChatPrompt={open_chat_prompt}
                    chatPromptState={chatPromptState}
                    events={activeConversationMessages}
                    participants={activeConversationMessageParticipants}
                    getConversationEvents={loadConversationEvents}
                    activeConversation={activeConversation}
                    avatarUrl={avatarUrl}
                    conversationLabel={conversationLabel}
                    bot={bot}
                    sendChat={sendChatWrap}
                    sendChatOrCreateRef={sendChatOrCreateRef}
                    showConversationHistory={() => setShowConversationHistory(true)}
                    bigPrompt={displayState === 'big'}
                    promptsToClosedPromptEvents={prompts_to_closed_prompt_events}
                    openChatPrompts={open_chat_prompt}
                    phone={phone}
                    closeChat={toggleOpenChatPublic}
                    setDisplayState={setDisplayState}
                    botParticipantID={botParticipantID}
                    unconnected={unconnected}
                    currentTypingEvent={currentTypingEvent}
                    fullScreen={fullScreen}
                    majorChatEvents={majorChatEvents}
                    cloudAgentName={cloudAgentName}
                    cloudAgentAvatar={cloudAgentAvatar}
                  />
                )}
              </div>
            </div>
          </div>
        ) : (
          <></>
        )}
        <ChatPrompt
          toggleOpenChat={toggleOpenChatPublic}
          openChat={displayState === 'open' || displayState === 'big'}
          welcomePacket={welcomePacket}
          packet_code={packet_code}
          showMiniPrompt={displayState === 'mini'}
          phone={phone}
          bigPrompt={displayState === 'big'}
          setDisplayState={setDisplayState}
          events={activeConversationMessages}
          hidden={fullScreen && displayState === 'open'}
        />
      </>
    </ThemeProvider>
  )
}

export default App
